import { useCallback, useEffect, useState } from 'react';
import Table from 'components/table';
import { RequestLogType, RootState } from 'types';
import { useParams } from 'react-router-dom';
import api from 'api';
import { toast } from 'react-toastify';
import './request-log.scss';
import { useSelector } from 'react-redux';
// import ReactTimeAgo from 'react-time-ago';
import { timeSince } from '../report-modal';

function RequestLog({
    selectReport
} : {
    selectReport : (data: number) => void
}) {    
    const state = useSelector((state: RootState) => state);
    const params: any = useParams();
    const [loading, setLoading] = useState<string>('');
    const [downloading, setDownloading] = useState<string>('');
    const [log, setLog] = useState<RequestLogType[]>([]);
    const [logPage, setLogPage] = useState<RequestLogType[]>([]);
    const [page, setPage] = useState<number>(1);    
    // const [showEndResults, setShowEndResults] = useState<boolean>(false);  

    const getLogs = useCallback(async () => {
        await api.report.getLogClient(params.clientId)
        .then((data: RequestLogType[]) => {
            var reportIds = state.reports.map(x => x.Id);
            setLog(data.filter(x => reportIds.includes(x.ReportId)));
        })
    }, [params.clientId, state.reports])

    const getContactName = (id: number) => {
        var contact = state.clientContacts.find(x => x.Id === id);
        return contact ? (contact?.Name + ' ' + contact?.Surname) : 'amaSocial';
    } 

    const getReportName = (id: number) => {
        var report = state.reports.find(x => x.Id === id);
        return report ? report?.Name : 'amaSocial Report';
    } 

    const getReportLayout = (id: number) => {
        var report = state.reports.find(x => x.Id === id);
        return report ? report?.LayoutId : 3;
    } 
    
    useEffect(() => {
        setLogPage(log.slice(0, 10*page));
    }, [log, page]);
    
    useEffect(() => {
        getLogs();
    }, [getLogs]);

    return (
        <div id={'request-log'} className={'table-container'}>
            <Table
                headers={{
                    Id: "",
                    ContactName: "CREATED BY",
                    ReportName: "REPORT NAME",
                    ReportGuid: "",
                    DateRequested: "REQUESTED",
                    DateRange: "DATE RANGE",
                    IsCompleted: "",
                    GenerateButton: "",
                    RegenerateButton: "",
                    DownloadButton: "",
                    SendButton: "",
                    DeleteButton: ""
                }}
                items={logPage.map(req => ({ 
                    Id: req.ReportId,
                    ContactName: getContactName(req.ContactId),
                    ReportName: getReportName(req.ReportId),
                    ReportGuid: req.Guid,
                    DateRequested: req.DateRequested,
                    DateRange: `${new Date(req.DateStart).toLocaleDateString()} - ${new Date(req.DateEnd).toLocaleDateString()}` ,
                    IsCompleted: req.IsCompleted,
                    GenerateButton: true,
                    RegenerateButton: true,
                    DownloadButton: true,
                    SendButton: true,
                    DeleteButton: true,
                }))}
                customRenderers={{
                    Id: () => (
                        null
                    ),
                    DateRequested: (item) => (
                        // <ReactTimeAgo date={new Date(item.DateRequested)} />
                        timeSince(new Date(item.DateRequested))
                    ),
                    IsCompleted: (item) => (
                        item.IsCompleted ? <i  title="Complete" className="fas fa-check success" /> : <i  title="Generating..." className="fas fa-spinner" />
                    ),
                    ReportGuid: () => (
                        null
                    ),
                    GenerateButton: (item) => (
                        <i title="Generate (New DR)" color='#4bb543' className="btn far fa-plus" onClick={() => selectReport(item.Id)} />
                    ),
                    RegenerateButton: (item) => (
                        <i title="Regenerate" color='#4bb543' className={loading === item.ReportGuid ? "fal fa-spinner fa-spin" : "btn fal fa-redo"} onClick={() => {setLoading(item.ReportGuid); regenerateReport(item.ReportGuid).then(getLogs).finally(() => setLoading(''))}} />
                    ),
                    DownloadButton: (item) => (
                        <i title="Download" className={downloading === item.ReportGuid ? "fal fa-spinner fa-spin" : "btn fas fa-file-download"} onClick={() => {setDownloading(item.ReportGuid); downloadReport(item.ReportGuid, getReportLayout(item.Id)).finally(() => setDownloading(''))}} />
                    ),
                    SendButton: (item) => (
                        <i title="Send" className="btn far fa-paper-plane" onClick={() => sendReport(item.ReportGuid)} />
                    ),
                    DeleteButton: (item) => (
                        <i title="Delete" className="btn far fa-trash-alt" onClick={() => (window.confirm('Are you sure you wish to delete this item?')) && deleteReport(item.ReportGuid)} />
                    ),
                }}
                />
                <div className='button-group'>
                    {logPage.length === log.length ?  <p id="end-results"> ~~~ </p> : <button className='btn-load' onClick={() => setPage(page+1)}><i className='fas fa-chevron-circle-down fa-2x'></i></button>}
                    {page > 1 ?  <button className='btn-load' onClick={() => setPage(page-1)}><i className='fas fa-chevron-circle-up fa-2x'></i></button> : '' }
                </div>
        </div>
    )
}

export default RequestLog;

async function regenerateReport(guid: string): Promise<void> {
    // console.log("Regenerate: ", guid);
    await api.report.regenerate(guid).then(() => {
        toast.success("Report regenerated");
    }).catch(err => {
        toast.error(err.toString());
    })
}


async function downloadReport(guid: string, layout:number): Promise<void> {
    // console.log("Download: ", guid);
    var fileFormat = "application/pdf";

    if (layout === 2){
        fileFormat = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }

    await api.report.download(guid, fileFormat)
    .then(() => {
        toast.success(`Downloaded`); return; 
    }).catch(err => {
        toast.error(err.toString());
    })
}


async function sendReport(guid: string): Promise<void> {
    // Open Contact List and send to selected contacts
    await api.report.send(guid).then(data => {
        toast.success(`Report sent to ${data} contact(s)`); return;
    }).catch(err => {
        toast.error(err.toString());
    });
}


async function deleteReport(guid: string): Promise<void> {
    // console.log("Delete: ", guid);
    await api.report.deleteRequest(guid).then(() => {
        toast.success("Report request deleted"); return;
    }).catch(err => {
        toast.error(err.toString());
    })
}
